import React, { useState } from 'react';
import { Download, Upload, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { exportMenuCSV, importMenuCSV, categorizeItems } from '../../services/importExportService';

const ImportExport = ({ onImportComplete }) => {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportMenuCSV();
      toast.success('Menu exported successfully!');
    } catch (error) {
      toast.error('Failed to export menu');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      toast.error('Please select a CSV or XLSX file');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const result = await importMenuCSV(file);
      setImportResult(result);
      
      if (result.errors && result.errors.length > 0) {
        toast.warning(`Import completed with ${result.errors.length} errors`);
      } else {
        toast.success('Menu imported successfully!');
      }
      
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      toast.error('Failed to import menu');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };



  return (
    <div className="glass rounded-2xl p-6">
      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--burgundy)' }}>
        <FileText size={24} />
        Import / Export Menu
      </h3>
      
      <div className="grid md:grid-cols-2 gap-6">
        {/* Export Section */}
        <div className="space-y-4">
          <h4 className="font-medium" style={{ color: 'var(--charcoal)' }}>Export Menu</h4>
          <p className="text-sm opacity-80" style={{ color: 'var(--charcoal)' }}>
            Download all menu items as a CSV file for editing
          </p>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Exporting...
              </>
            ) : (
              <>
                <Download size={20} />
                Export to CSV
              </>
            )}
          </button>
        </div>

        {/* Import Section */}
        <div className="space-y-4">
          <h4 className="font-medium" style={{ color: 'var(--charcoal)' }}>Import Menu</h4>
          <p className="text-sm opacity-80" style={{ color: 'var(--charcoal)' }}>
            Upload a CSV or XLSX file to update menu items
          </p>
          <label className="btn-secondary w-full flex items-center justify-center gap-2 cursor-pointer">
            {importing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: 'var(--burgundy)' }}></div>
                Importing...
              </>
            ) : (
              <>
                <Upload size={20} />
                Select CSV/XLSX File
              </>
            )}
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={handleImport}
              disabled={importing}
              className="hidden"
            />
          </label>
        </div>


      </div>



      {/* Import Results */}
      {importResult && (
        <div className="mt-6 p-4 rounded-lg border" style={{ 
          backgroundColor: importResult.errors?.length > 0 ? '#fef3cd' : '#d1f2eb',
          borderColor: importResult.errors?.length > 0 ? '#ffc107' : '#28a745'
        }}>
          <div className="flex items-start gap-2">
            <AlertCircle size={20} style={{ 
              color: importResult.errors?.length > 0 ? '#856404' : '#155724',
              marginTop: '2px'
            }} />
            <div className="flex-1">
              <h5 className="font-medium" style={{ 
                color: importResult.errors?.length > 0 ? '#856404' : '#155724'
              }}>
                Import Results
              </h5>
              <p className="text-sm mt-1" style={{ 
                color: importResult.errors?.length > 0 ? '#856404' : '#155724'
              }}>
                {importResult.message}
              </p>
              
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium mb-2" style={{ color: '#856404' }}>
                    Errors ({importResult.errors.length}):
                  </p>
                  <div className="max-h-32 overflow-y-auto">
                    {importResult.errors.slice(0, 5).map((error, index) => (
                      <p key={index} className="text-xs mb-1" style={{ color: '#856404' }}>
                        • {error}
                      </p>
                    ))}
                    {importResult.errors.length > 5 && (
                      <p className="text-xs" style={{ color: '#856404' }}>
                        ... and {importResult.errors.length - 5} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
        <h5 className="font-medium mb-2" style={{ color: 'var(--charcoal)' }}>Instructions:</h5>
        <ul className="text-sm space-y-1" style={{ color: 'var(--charcoal)', opacity: 0.8 }}>
          <li>• Export the current menu to get the correct CSV format</li>
          <li>• Edit the file with your changes (supports CSV and XLSX formats)</li>
          <li>• Keep the 'id' column unchanged to update existing items</li>
          <li>• Use semicolons (;) to separate multiple prices (e.g., "250;350;450")</li>
          <li>• Set 'available' to true/false to control item visibility</li>
          <li>• Supported outlets: cafe-plus, plus-one</li>
          <li>• Supported veg options: veg, non-veg, both, none</li>
        </ul>
        
        <div className="mt-3 p-3 rounded" style={{ backgroundColor: '#e9ecef' }}>
          <p className="text-xs font-medium mb-1" style={{ color: 'var(--charcoal)' }}>CSV Format Example:</p>
          <code className="text-xs" style={{ color: 'var(--charcoal)' }}>
            id,name,description,prices_in_inr,category,outlet,veg,available<br/>
            item123,Butter Chicken,Rich and creamy chicken curry,350;450,main-course,cafe-plus,non-veg,true
          </code>
        </div>
      </div>
    </div>
  );
};

export default ImportExport;